import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Pause, Play, OctagonX, PlugZap, CheckCircle2, XCircle, CalendarClock, MailCheck, X } from "lucide-react";

const STEP = (s) => (s === 0 ? "Toestemming" : `Mail ${s}`);
const t = (iso) => (iso ? new Date(iso).toLocaleString("nl-NL", { weekday: "short", hour: "2-digit", minute: "2-digit" }) : "");

const sendTest = async (API, email, step) => {
  const tid = toast.loading(`Testmail (${STEP(step)}) versturen vanaf ${email}…`);
  try {
    const { data } = await axios.post(`${API}/mail/senders/${encodeURIComponent(email)}/testmail`, null, { params: { step } });
    toast.success(`Testmail verstuurd naar ${data.to} (voorbeeld: ${data.sample})${data.sent_folder ? " — kopie in Verzonden" : ""}`, { id: tid, duration: 10000 });
  } catch (e) { toast.error(e?.response?.data?.detail || "Testmail mislukt", { id: tid }); }
};

const SenderRow = ({ API, s, onSaved, step }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try {
      const { data } = await fn();
      data.ok ? toast.success(`${s.email}: verbinding OK${data.sent_folder ? ` (Verzonden-map: ${data.sent_folder})` : ""}`)
        : toast.error(`${s.email}: ${data.error}`);
      setPw("");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Mislukt"); }
    setBusy(false);
  };
  const lt = s.last_test;
  return (
    <div data-testid={`hostnet-sender-${s.email}`} className="flex flex-wrap items-center gap-2 py-1.5 min-w-0">
      <span className="w-40 truncate text-sm font-medium" title={s.email}>{s.email}</span>
      {lt ? (lt.ok ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> verbonden</span>
        : <span className="inline-flex items-center gap-1 text-xs text-red-600" title={lt.error}><XCircle className="w-3.5 h-3.5" /> fout</span>)
        : <span className="text-xs text-slate-400">{s.has_password ? "niet getest" : "geen wachtwoord"}</span>}
      <Input data-testid={`hostnet-pw-${s.email}`} type="password" autoComplete="new-password" placeholder="Hostnet-wachtwoord"
        value={pw} onChange={(e) => setPw(e.target.value)} className="h-8 w-36 text-sm" />
      <Button data-testid={`hostnet-pw-save-${s.email}`} size="sm" variant="outline" disabled={!pw || busy}
        onClick={() => run(() => axios.put(`${API}/mail/senders`, { email: s.email, password: pw }))}>Opslaan & testen</Button>
      {s.has_password && (
        <Button data-testid={`hostnet-test-${s.email}`} size="sm" variant="ghost" disabled={busy}
          title="Verbinding testen" onClick={() => run(() => axios.post(`${API}/mail/senders/${encodeURIComponent(s.email)}/test`))}>
          <PlugZap className="w-3.5 h-3.5" />
        </Button>
      )}
      {s.has_password && (
        <Button data-testid={`hostnet-testmail-${s.email}`} size="sm" variant="ghost" onClick={() => sendTest(API, s.email, step)}>
          <MailCheck className="w-3.5 h-3.5 mr-1" /> Testmail
        </Button>
      )}
    </div>
  );
};

const QueueList = ({ title, items, testid, onRemove }) => (
  <div data-testid={testid}>
    <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">{title}</div>
    {items.length ? (
      <div className="max-h-48 overflow-auto rounded border border-slate-100 divide-y divide-slate-100 text-xs">
        {items.map((i) => (
          <div key={i.id} className="flex items-center gap-2 px-2 py-1.5">
            <span className="w-24 shrink-0 text-slate-500">{STEP(i.step)}</span>
            <span className="flex-1 min-w-0" title={i.error || i.subject || ""}>
              <span className="block truncate">{i.name}</span>
              <span className="block truncate text-slate-400">{i.to} · via {i.sender}</span>
            </span>
            <span className={`w-24 shrink-0 text-right ${i.status === "failed" ? "text-red-600" : i.status === "sent" ? "text-emerald-700" : "text-slate-500"}`}
              title={i.error || i.imap_error || ""}>
              {i.status === "sent" ? `verzonden ${t(i.sent_at)}` : i.status === "failed" ? "mislukt" : i.status === "cancelled" ? "geannuleerd" : i.status === "sending" ? "bezig…" : "in wachtrij"}
            </span>
            {onRemove && i.status === "queued" && (
              <button data-testid={`hostnet-remove-${i.id}`} title="Uit de wachtrij halen" aria-label="Uit de wachtrij halen" onClick={() => onRemove(i)}
                className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
    ) : <p className="text-xs text-slate-400">Leeg.</p>}
  </div>
);

export const HostnetPanel = ({ API, profile, onChanged }) => {
  const [q, setQ] = useState(null);
  const [step, setStep] = useState(0);
  const load = useCallback(() => axios.get(`${API}/mail/queue`).then(({ data }) => setQ(data)).catch(() => {}), [API]);
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [load]);
  if (!q) return null;
  const ms = q.settings;
  const putSettings = async (patch, el) => {
    try { await axios.put(`${API}/mail/settings`, patch); load(); } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
      if (el) el.value = el.dataset.prev;
    }
  };
  const num = (key, scale = 1) => ({
    "data-prev": Math.round(ms[key] / scale), defaultValue: Math.round(ms[key] / scale),
    onBlur: (e) => Number(e.target.value) * scale !== ms[key] && putSettings({ [key]: Number(e.target.value) * scale }, e.target),
  });
  const planProfile = async () => {
    try {
      const { data: pre } = await axios.post(`${API}/mail/schedule`, { profile, step, dry_run: true });
      if (!pre.eligible) return toast.info(pre.already_queued
        ? `${pre.already_queued} lead(s) staan al in de wachtrij voor verzending — zie de wachtrij hieronder.`
        : `Geen leads aan de beurt voor ${STEP(step)} (al gevraagd, afgemeld of geen functie/naam bekend).`, { duration: 10000 });
      if (!window.confirm(`${STEP(step)} inplannen voor ${pre.eligible} lead(s) in dit profiel?`)) return;
      const { data } = await axios.post(`${API}/mail/schedule`, { profile, step });
      toast.success(`${data.queued} mail(s) ingepland`);
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Inplannen mislukt"); }
  };
  const stopAll = async () => {
    if (!window.confirm("Noodstop: alle mails in de wachtrij annuleren?")) return;
    try {
      const { data } = await axios.post(`${API}/mail/queue/cancel`, {});
      toast.success(`${data.cancelled} mail(s) geannuleerd`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Annuleren mislukt"); }
  };
  const removeOne = async (i) => {
    try {
      const { data } = await axios.post(`${API}/mail/queue/cancel`, { ids: [i.id] });
      data.cancelled ? toast.success(`${i.name || i.to} uit de wachtrij gehaald`) : toast.info("Deze mail wordt al verzonden of is al weg");
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verwijderen mislukt"); }
  };
  const c = q.counts || {};
  return (
    <div data-testid="hostnet-panel" className="rounded-xl border border-slate-200 p-4 flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold text-slate-900 mr-auto">Verzenden via Hostnet</h3>
        <span data-testid="hostnet-status" className={`text-xs rounded-full px-2 py-0.5 ${ms.paused ? "bg-amber-100 text-amber-800" : q.in_window ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
          {ms.paused ? "Gepauzeerd" : q.in_window ? "Actief — verzendt nu" : `Buiten verzendtijd (ma–vr ${ms.start_hour}:00–${ms.end_hour}:00)`}
        </span>
        <Button data-testid="hostnet-pause-toggle" size="sm" variant="outline" onClick={() => putSettings({ paused: !ms.paused })}>
          {ms.paused ? <><Play className="w-3.5 h-3.5 mr-1" /> Hervat</> : <><Pause className="w-3.5 h-3.5 mr-1" /> Pauzeer</>}
        </Button>
        <Button data-testid="hostnet-stop-all" size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={stopAll}>
          <OctagonX className="w-3.5 h-3.5 mr-1" /> Noodstop
        </Button>
      </div>

      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Afzenders</div>
        {q.senders.length ? q.senders.map((s) => (
          <div key={s.email} className="flex flex-wrap lg:flex-nowrap items-center gap-2">
            <SenderRow API={API} s={s} onSaved={load} step={step} />
            <span className="ml-auto text-xs text-slate-500 tabular-nums text-right">
              vandaag {q.today[s.email] || 0}/{ms.daily_max} · wachtrij {s.queued}
              {s.queued > 0 && s.blocker && <span data-testid={`hostnet-blocker-${s.email}`} className={`block ${/wachtwoord|mislukt/.test(s.blocker) ? "text-red-600 font-semibold" : "text-slate-400"}`}>{s.blocker}</span>}
            </span>
          </div>
        )) : <p className="text-xs text-slate-500">Vul hierboven eerst je Hostnet-adressen in bij <b>Afzenders</b>.</p>}
        <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-slate-600">
          <span>Testadres (je eigen adres):</span>
          <Input data-testid="hostnet-test-to" type="email" className="h-8 w-56" placeholder="jij@voorbeeld.nl" defaultValue={ms.test_to}
            data-prev={ms.test_to} onBlur={(e) => e.target.value.trim() !== ms.test_to && putSettings({ test_to: e.target.value.trim() }, e.target)} />
          <span className="text-slate-400">"Testmail" stuurt de mail die hieronder bij Inplannen gekozen is (Toestemming of Mail 1–3, met foto), ingevuld voor een voorbeeld-lead, met [TEST] in het onderwerp. Er wordt niets bij een lead geteld.</span>
        </div>
        <p className="text-[11px] text-slate-400 mt-1">Wachtwoorden worden versleuteld opgeslagen en nooit getoond. Elke verzonden mail komt ook in je Verzonden-map bij Hostnet.</p>
      </div>

      {q.orphan_queued > 0 && (
        <p data-testid="hostnet-orphan" className="text-xs text-red-600">{q.orphan_queued} mail(s) in de wachtrij horen bij een afzender die niet meer in je lijst staat. Ze worden niet verstuurd: bij de volgende verzendronde komen ze op "mislukt" en kun je die leads opnieuw inplannen.</p>
      )}
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <span>Werkdagen</span>
        <Input data-testid="hostnet-start" type="number" className="h-8 w-14" {...num("start_hour")} />
        <span>tot</span>
        <Input data-testid="hostnet-end" type="number" className="h-8 w-14" {...num("end_hour")} />
        <span>uur · elke</span>
        <Input data-testid="hostnet-gap-min" type="number" className="h-8 w-14" {...num("gap_min", 60)} />
        <span>–</span>
        <Input data-testid="hostnet-gap-max" type="number" className="h-8 w-14" {...num("gap_max", 60)} />
        <span>min per adres · max</span>
        <Input data-testid="hostnet-daily-max" type="number" className="h-8 w-16" {...num("daily_max")} />
        <span>per dag per adres</span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="hostnet-plan-step" value={step} onChange={(e) => setStep(Number(e.target.value))} className="h-9 rounded-md border border-slate-200 px-2 text-sm">
          <option value={0}>Toestemmingsmail (leads met functie/naam)</option>
          <option value={1}>Mail 1 (alleen met toestemming)</option>
          <option value={2}>Mail 2 (alleen met toestemming)</option>
          <option value={3}>Mail 3 (alleen met toestemming)</option>
        </select>
        <Button data-testid="hostnet-plan-profile" size="sm" onClick={planProfile} className="bg-[#FF4500] hover:bg-[#E63E00] text-white">
          <CalendarClock className="w-4 h-4 mr-1.5" /> Inplannen voor dit profiel
        </Button>
        <span data-testid="hostnet-counts" className="ml-auto text-xs text-slate-500">
          wachtrij {c.queued || 0} · verzonden {c.sent || 0} · mislukt {c.failed || 0}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <QueueList title="Wachtrij" items={q.upcoming} testid="hostnet-upcoming" onRemove={removeOne} />
        <QueueList title="Recent" items={q.recent} testid="hostnet-recent" />
      </div>
    </div>
  );
};
